import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import Pulse from "../Loader/Pulse";
import {
  ArrowLeft,
  ArrowRight2,
  Cross,
  OrderBook,
} from "../CustomComponents/SwiftIcon/Icons";
import { COLOR_VARS } from "../../exports/ColorVars";
import ServerRequest from "../../exports/ServerRequest";
import { GetCookie } from "../../exports/CookieManagement";
import SwiftModal from "../CustomComponents/SwiftModal/SwiftModal";
import CustomInputFile from "../CustomComponents/CustomInputFile/CustomInputFile";
import {
  decodeToken,
  numberFormat,
  priceFormat,
  formatSchemeName,
} from "../../exports/UtilFunctions";
import CustomIconButton from "../Body/CustomChartComponents/CustomIconButton/CustomIconButton";
import { getDeviceSize } from "../../exports/InteractiveUtils";
import { Alert } from "../Body/CustomChartComponents/CustomAlert/CustomAlert";
import { ConfirmationBoxSmart } from "./ConfirmationBox";
import linkButton from "../../assets/icons/link.svg";
import cancelButton from "../../assets/icons/sync-cancel.svg";
import Search from "../../assets/icons/search.svg";
import Badge from "../ui/badge";

/** =============================
 *  Constants & Helpers
 *  ============================= */
const DEVICE_SIZE = getDeviceSize();

const STATUS = {
  APPROVAL_PENDING: "Approval Pending",
  UTR_PENDING: "UTR Pending",
};

const PAYMENT_TYPE = {
  UPI: "UPI",
  NETBANKING: "NETBANKING",
  NEFT: "NEFT",
};

const warning_config = {
  Band: true,
  BandColor: "var(--main-blue)",
  BoxColor: "var(--white)",
  TextColor: "var(--text-color)",
  AutoClose: {
    Active: true,
    Line: true,
    LineColor: "var(--main-blue)",
    Time: 3,
  },
};

/** =============================
 *  Custom Hooks
 *  ============================= */
function useTransactions({ account_code, account_token, customer, reloadKey }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function fetchTransaction() {
      if (!account_code && !account_token) return;
      setLoading(true);
      try {
        const url = customer
          ? `/mf/orderbook?account_code=${account_code}`
          : `/mf/orderbook?token=${account_token}`;
        const response = await ServerRequest({ url, method: "get" });
        if (mounted) setTransactions(response.data || []);
      } catch (e) {
        console.error("Transaction fetch error", e);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchTransaction();
    return () => {
      mounted = false;
    };
  }, [account_code, account_token, customer, reloadKey]);

  return { transactions, loading, setTransactions };
}

function useClientAccounts({ account_code, account_token, customer }) {
  const [clientData, setClientData] = useState([]);

  useEffect(() => {
    let mounted = true;
    async function fetchClientMasterReport() {
      if (!account_code && !account_token) return;
      try {
        const url = !customer
          ? `/nse/get-client-account?token=${account_token}`
          : `/nse/get-client-account?account_code=${account_code}`;
        const res = await ServerRequest({ url, method: "get" });
        if (mounted) setClientData(res?.data?.report_data || []);
      } catch (err) {
        console.error("Client account fetch error", err);
      }
    }
    fetchClientMasterReport();
    return () => {
      mounted = false;
    };
  }, [account_code, account_token, customer]);

  return clientData;
}

/** =============================
 *  Main Component
 *  ============================= */
export default function Transaction({ customer }) {
  /** Basics */
  const account_token = GetCookie("token");
  const account_code = customer?.account_code;

  const [reloadKey, setReloadKey] = useState(0);

  /** Data */
  const { transactions, loading } = useTransactions({
    account_code,
    account_token,
    customer,
    reloadKey,
  });
  const clientData = useClientAccounts({
    account_code,
    account_token,
    customer,
  });

  /** UI: progressive loading text */
  const [loadingMsg, setLoadingMsg] = useState("Fetching your transactions");
  const [opacityMsg, setOpacityMsg] = useState(true);

  useEffect(() => {
    const loadingMessageInterval = setInterval(() => {
      setLoadingMsg((prev) =>
        prev === "Fetching your transactions"
          ? "It may take some time"
          : "Fetching your transactions",
      );
    }, 3000);
    const opacityInterval = setInterval(
      () => setOpacityMsg((prev) => !prev),
      1500,
    );
    return () => {
      clearInterval(loadingMessageInterval);
      clearInterval(opacityInterval);
    };
  }, []);

  /** Search & suggestions */
  const [search, setSearch] = useState("");
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target))
        setSuggestionOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const uniqueStockSuggestion = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = (transactions || []).filter((item) =>
      term.length === 0 ? true : item.fund_name?.toLowerCase().includes(term),
    );
    return Array.from(
      new Map(filtered.map((item) => [item.fund_name, item])).values(),
    );
  }, [search, transactions]);

  const filteredItems = useMemo(() => {
    if (selectedSuggestion) {
      return (transactions || []).filter(
        (item) => item.fund_name === selectedSuggestion.fund_name,
      );
    }
    const term = search.trim().toLowerCase();
    if (!term) return transactions || [];
    return (transactions || []).filter((item) =>
      item.fund_name?.toLowerCase().includes(term),
    );
  }, [search, transactions, selectedSuggestion]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setSelectedSuggestion(null);
    setSuggestionOpen(value.trim().length > 0);
  };
  const handleInputClick = () => {
    if (selectedSuggestion) {
      setSearch("");
      setSelectedSuggestion(null);
    }
    if (search.trim().length > 0) setSuggestionOpen(true);
  };
  const clearSearch = () => {
    setSearch("");
    setSelectedSuggestion(null);
    setSuggestionOpen(false);
  };

  /** Popup State */
  const [popup, setPopup] = useState({
    open: false,
    loading: false,
    data: [],
    isCancel: false,
    isUpi: false,
    vpaData: null,
    vpa: "",
    step: "Process", // "Process" | "confirm"
    confirmMessage: "",
    cancelMessage: "",
  });

  const openPopup = useCallback(
    (next) => setPopup((p) => ({ ...p, open: true, ...next })),
    [],
  );
  const setPopupLoading = useCallback(
    (loading) => setPopup((p) => ({ ...p, loading })),
    [],
  );

  const resetPopup = useCallback(() => {
    setPopup({
      open: false,
      loading: false,
      data: [],
      isCancel: false,
      isUpi: false,
      vpaData: null,
      vpa: "",
      step: "Process",
      confirmMessage: "",
      cancelMessage: "",
    });
    setReloadKey((k) => k + 1);
  }, []);

  /** Handlers */
  const handleInputUTR = async (row, type) => {
    setPopupLoading(true);
    openPopup({ open: true, isCancel: false });
    try {
      const response = await ServerRequest({
        url: `/mf/fetchorderbyinternalreferenceno?internal_no=${row.internal_order_id}`,
        method: "get",
      });
      const data = response?.data || [];
      if (type === PAYMENT_TYPE.UPI) {
        const decoded = decodeToken(data[0]?.payment_link);
        setPopup((p) => ({
          ...p,
          data,
          isUpi: true,
          vpaData: decoded,
          vpa: decoded?.vpa || "",
        }));
      } else {
        setPopup((p) => ({
          ...p,
          data,
          isUpi: false,
          vpaData: data[0]?.payment_link || null,
        }));
      }
    } catch (error) {
      console.error("Error: ", error);
    } finally {
      setPopupLoading(false);
    }
  };

  const handlerCancel = async (row) => {
    setPopupLoading(true);
    openPopup({ open: true, isCancel: true });
    try {
      const response = await ServerRequest({
        url: `/mf/fetchorderbyinternalreferenceno?internal_no=${row.internal_order_id}`,
        method: "get",
      });
      setPopup((p) => ({ ...p, data: response?.data || [] }));
      console.log(response.data);
    } catch (e) {
      console.error("Cancel prefetch error", e);
    } finally {
      setPopupLoading(false);
    }
  };

  const submitUpiResend = async () => {
    setPopupLoading(true);
    try {
      if (!popup.vpa) {
        Alert({
          TitleText: "Warning",
          Message: "Please enter UPI/VPA id.",
          ...warning_config,
        });
        return;
      }
      const payload = {
        vpaData: { ...(popup.vpaData || {}), vpa: popup.vpa },
        selectedTxnType: PAYMENT_TYPE.UPI,
        transaction_details: popup.data,
        clientAccounts: clientData,
      };
      const response = await ServerRequest({
        url: `/nse/resend-upi-request`,
        method: "post",
        data: payload,
      });

      const isSuccess = response?.data?.status?.toLowerCase?.() === "success";
      setPopup((p) => ({
        ...p,
        step: "confirm",
        confirmMessage: isSuccess ? "Success" : "Failed",
        cancelMessage: response?.data?.remark
          ? isSuccess
            ? response?.data?.remark
            : `You can only generate UPI request again after 15 minutes or if it has expired. If your request has expired, then you will be able to generate request again.\n\n${response?.data?.remark}`
          : isSuccess
            ? "UPI request sent successfully"
            : "UPI request failed",
      }));
    } catch (err) {
      console.error("Error in submitUpiResend:", err);
      setPopup((p) => ({
        ...p,
        step: "confirm",
        confirmMessage: "Failed",
        cancelMessage: "Something went wrong. Please try again.",
      }));
    } finally {
      setPopupLoading(false);
    }
  };

  const submitNetbankingResend = async () => {
    setPopupLoading(true);
    try {
      const response = await ServerRequest({
        url: `/nse/resend-payment-request`,
        method: "post",
        data: {
          payment_link: popup?.data?.[0]?.payment_link,
          selectedTxnType: PAYMENT_TYPE.NETBANKING,
          transaction_details: popup.data,
          clientAccounts: clientData,
        },
      });
      const ok = response?.success === true;
      setPopup((p) => ({
        ...p,
        step: "confirm",
        confirmMessage: ok ? "Success" : "Failed",
        cancelMessage: ok
          ? "Payment link sent successfully"
          : "Payment request failed",
      }));
    } catch (err) {
      console.error("Error in submitNetbankingResend:", err);
      setPopup((p) => ({
        ...p,
        step: "confirm",
        confirmMessage: "Failed",
        cancelMessage: "Something went wrong. Please try again.",
      }));
    } finally {
      setPopupLoading(false);
    }
  };

  const submitCancelOrders = async () => {
    setPopupLoading(true);
    try {
      const res = await ServerRequest({
        url: "nse/cancel-order",
        method: "post",
        data: { transactions: popup.data, clientData },
      });
      const cancelDataArray = res?.response?.can_data || [];
      if (cancelDataArray.length > 0) {
        const allSuccess = cancelDataArray.every(
          (d) => d.can_status === "CAN SUCCESS",
        );
        const messages = cancelDataArray.map(
          (d) => d.can_remark || "No remark",
        );
        setPopup((p) => ({
          ...p,
          step: "confirm",
          confirmMessage: allSuccess ? "CSuccess" : "CFailed",
          cancelMessage: allSuccess
            ? "Your order has been cancelled"
            : messages,
        }));
      } else {
        setPopup((p) => ({
          ...p,
          step: "confirm",
          confirmMessage: "CFailed",
          cancelMessage: "Unexpected response from server.",
        }));
      }
    } catch (error) {
      console.error("Cancel API Error:", error);
      setPopup((p) => ({
        ...p,
        step: "confirm",
        confirmMessage: "CFailed",
        cancelMessage: "Server error while cancelling order.",
      }));
    } finally {
      setPopupLoading(false);
    }
  };

  const handlePopupSubmit = () => {
    if (popup.step === "confirm") return;
    if (popup.isCancel) return submitCancelOrders();
    if (popup.isUpi) return submitUpiResend();
    return submitNetbankingResend();
  };

  /** Render */
  return (
    <div className="mf-wrapper">
      {/* Search */}
      <div className="transaction-search-wrapper stock__search__icons__wrapper">
        {transactions.length > 0 && (
          <div className="stock__search" ref={ref}>
            <div className="stock__search__icon">
              <img src={Search} alt="" />
            </div>
            <input
              placeholder="Search Transactions"
              value={search}
              onChange={handleSearch}
              onClick={handleInputClick}
              autoComplete="off"
            />
            {search && selectedSuggestion && (
              <div className="portfolio-mfwc-buttons">
                <div className="hover-btn" onClick={clearSearch}>
                  <Cross size={30} color="black" />
                </div>
              </div>
            )}
            {suggestionOpen && (
              <div className="stock__suggestions">
                <div className="suggestion__stocks">
                  <StockSuggestion
                    search={search}
                    loading={loading}
                    suggestions={uniqueStockSuggestion}
                    handleSelect={(item) => {
                      setSearch(item.fund_name || "");
                      setSelectedSuggestion(item);
                      setSuggestionOpen(false);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div
        className={`mf-container ${customer ? "short" : ""} mf-transaction-container mf-container`}
      >
        {loading ? (
          <div className="app__login__screen">
            <div className="app__login__process">
              <Pulse />
              {/* <p className={opacityMsg ? "fadeIn" : "fadeOut"}>{loadingMsg}</p> */}
            </div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="mf-empty">
            <OrderBook size={40} color={COLOR_VARS["SWIFT_COLOR4"]} />
            <div>
              <p>No Transactions found</p>
              <span>Looks like you have't made any Transactions</span>
            </div>
          </div>
        ) : DEVICE_SIZE === "S" ? (
          <>
            {filteredItems.map((h, index) => (
              <TransactionCard
                key={index}
                data={h}
                onResend={(row, type) => handleInputUTR(row, type)}
                onCancel={(row) => handlerCancel(row)}
                customer={customer}
              />
            ))}
          </>
        ) : (
          <TransactionTable
            items={filteredItems}
            onResend={(row, type) => handleInputUTR(row, type)}
            onCancel={(row) => handlerCancel(row)}
            customer={customer}
          />
        )}
      </div>

      {/* Popup */}
      {popup.open && (
        <SwiftModal closeModal={resetPopup} top="2%">
          <div
            className={`${popup.step === "confirm" ? "confirm" : ""} swift-mf-popup`}
          >
            {popup.loading ? (
              <div className="swift-mf-popup-loader">
                <Pulse />
{/* <p>Transaction in progress</p> */}
              </div>
            ) : (
              <>
                {popup.step === "confirm" ? (
                  <div className="swift-mf-popup-body">
                    <div className="swift-mf-popup-details">
                      <ConfirmationBoxSmart
                        confirmMessage={popup.confirmMessage}
                        cancelMessage={popup.cancelMessage}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="swift-mf-popup-header">
                      <div className={`swift-mf-popup-header-title`}>
                        {popup.isCancel
                          ? "Cancel Order(s)"
                          : popup.isUpi
                            ? "Resend UPI Request"
                            : "Resend Payment Link"}
                      </div>
                      <div className="swift-account-svg" onClick={resetPopup}>
                        {DEVICE_SIZE === "S" ? (
                          <ArrowLeft
                            size={25}
                            color={COLOR_VARS["SWIFT_COLOR4"]}
                          />
                        ) : (
                          <Cross size={30} color={COLOR_VARS["SWIFT_COLOR4"]} />
                        )}
                      </div>
                    </div>
                    <div className="swift-mf-popup-text">
                      {popup.isCancel
                        ? "The below orders will be cancelled."
                        : "We will confirm the success or failure of the transaction shortly."}
                    </div>
                    <div className="swift-mf-popup-body">
                      <div className="swift-mf-popup-details">
                        {(popup.data || []).map((order) => (
                          <div
                            className="popup-card"
                            key={order.transaction_ref_number}
                          >
                            <p className="swift-mf-popup-isin">
                              {order.transaction_ref_number}
                            </p>
                            <p className="swift-mf-popup-name">
                              {order.fund_name}
                            </p>
                            <p className="swift-mf-popup-isin">
                              {priceFormat(order.amount)}
                            </p>
                          </div>
                        ))}
                      </div>
                      {!popup.isCancel && popup.isUpi && (
                        <CustomInputFile
                          name="utr_number"
                          type="text"
                          value={popup.vpa}
                          className="swift-mf-bonds-inputs"
                          DivClassName="swift-mf-bonds-inputs-div"
                          placeholder="UPI/ VPA ID"
                          onchange={(name, value) =>
                            setPopup((p) => ({ ...p, vpa: value }))
                          }
                        />
                      )}
                    </div>
                  </div>
                )}
                <div className="swift-mf-popup-submit-btn">
                  {popup.step === "confirm" ? (
                    <CustomIconButton
                      title="Close"
                      icon={
                        {/* <ArrowRight2
                          size={20}
                          color={COLOR_VARS.SWIFT_COLOR6}
                        /> */}
                      }
                      onClick={resetPopup}
                    />
                  ) : (
                    <CustomIconButton
                      title="Submit"
                      icon={
                        {/* <ArrowRight2
                          size={20}
                          color={COLOR_VARS.SWIFT_COLOR6}
                        /> */}
                      }
                      onClick={handlePopupSubmit}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </SwiftModal>
      )}
    </div>
  );
}

/** =============================
 *  Table (Desktop)
 *  ============================= */
function TransactionTable({ items, onResend, onCancel, customer }) {
  return (
    <table>
      <thead>
        <tr>
          <td>Date</td>
          <td className="scheme-name">Scheme Name</td>
          <td>ISIN</td>
          <td>Scheme Code</td>
          <td>Folio</td>
          <td>Buy/Sell</td>
          <td>Transaction Type</td>
          <td>Units</td>
          <td>NAV</td>
          <td>Amount</td>
          <td className="status">Status</td>
          <td className="hover-td"></td>
        </tr>
      </thead>
      <tbody>
        {items.map((h, index) => (
          <tr
            key={index}
            className={h.status === STATUS.UTR_PENDING ? "yes_hover" : ""}
          >
            <td>{h.input_date}</td>
            <td title={h.fund_name} className="body-scheme-name">
              {formatSchemeName(h.fund_name, 0, false)}
            </td>
            <td>{h.isin}</td>
            <td>{h.scheme_code}</td>
            <td>{h.folio_no}</td>
            <td className={h.type === "Buy" ? "positive" : "negative"} style={{textTransform:"uppercase"}}>
              • {h.type}
            </td>
            <td>{h.tran_type}</td>
            <td>{numberFormat(h.units)}</td>
            <td>{priceFormat(h.tran_nav)}</td>
            <td>{priceFormat(h.amount)}</td>
            <td>
              <Badge
                variant={
                  h.status === "Completed"
                    ? "success"
                    : h.status === "Approval Pending" || h.status === "Pending"
                      ? "warning"
                      : "error"
                }
                size="xs"
              >
                {h.status}
              </Badge>
            </td>
            <td>
              {customer && (
                <div className="hover-container">
                  {h.status === STATUS.APPROVAL_PENDING &&
                    h.payment_type === PAYMENT_TYPE.NEFT && (
                      <>
                        <div
                          className="hover-btn"
                          onClick={() =>
                            window.open(`${h.payment_link}`, "_blank")
                          }
                        >
                          <img src={linkButton} alt="linkButton" /> Authorise
                          Transaction
                        </div>
                        <div className="hover-btn" onClick={() => onCancel(h)}>
                          {" "}
                          <img src={cancelButton} alt="cancelButton" /> Cancel
                        </div>
                      </>
                    )}
                  {h.status === STATUS.APPROVAL_PENDING &&
                    h.payment_type === PAYMENT_TYPE.UPI && (
                      <>
                        <div
                          className="hover-btn"
                          onClick={() => onResend(h, PAYMENT_TYPE.UPI)}
                        >
                          {" "}
                          <img src={linkButton} alt="linkButton" />
                          Resend Request
                        </div>
                        <div className="hover-btn" onClick={() => onCancel(h)}>
                          <img src={cancelButton} alt="cancelButton" /> Cancel
                        </div>
                      </>
                    )}
                  {h.status === STATUS.APPROVAL_PENDING &&
                    h.payment_type === PAYMENT_TYPE.NETBANKING && (
                      <>
                        <div
                          className="hover-btn"
                          onClick={() => onResend(h, PAYMENT_TYPE.NETBANKING)}
                        >
                          <img src={linkButton} alt="linkButton" /> Resend Link
                        </div>
                        <div className="hover-btn" onClick={() => onCancel(h)}>
                          <img src={cancelButton} alt="cancelButton" /> Cancel
                        </div>
                      </>
                    )}
                  {h.status === STATUS.APPROVAL_PENDING &&
                    h.type === "Sell" && (
                      <>
                        <div className="hover-btn" onClick={() => onCancel(h)}>
                          <img src={cancelButton} alt="cancelButton" /> Cancel
                        </div>
                      </>
                    )}
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** =============================
 *  Card (Mobile)
 *  ============================= */
function TransactionCard({ data, onResend, onCancel, customer }) {
  const {
    isin,
    input_date,
    amount,
    type,
    fund_name,
    tran_type,
    tran_nav,
    scheme_code,
    status,
    units,
    folio_no,
    payment_link,
    payment_type,
  } = data;
  const [fundName, setFundName] = useState(null);
  useEffect(() => {
    if (fund_name) {
      setFundName(formatSchemeName(fund_name));
    }
  }, [fund_name]);
  return (
    // <div className="mf-watchlist-card">
    //   <div className="mfwc-info">
    //     <div className="isin-price">
    //       <p className="isin-type">{input_date}</p>
    //       <p className=" sip-cease-txt right-top-txt">{status}</p>
    //     </div>
    //     <p className="name">{fund_name}</p>
    //   </div>
    //   <div className="mfwc-config">
    //     <div className="mfwc-config-row">
    //       <p>ISIN <span>{isin}</span></p>
    //       <p>Scheme Code <span>{scheme_code}</span></p>
    //     </div>
    //     <div className="mfwc-config-row">
    //       <p>Folio <span>{folio_no}</span></p>
    //       <p>Buy/Sell <span>{type}</span></p>
    //     </div>
    //     <div className="mfwc-config-row">
    //       <p>Transaction Type <span>{tran_type}</span></p>
    //       <p>NAV <span>{priceFormat(tran_nav)}</span></p>
    //     </div>
    //     <div className="mfwc-config-row">
    //       <p>Units <span>{numberFormat(units)}</span></p>
    //       <p>Amount <span>{priceFormat(amount)}</span></p>
    //     </div>
    //   </div>
    //   {/* Actions (uncomment if you want buttons on mobile cards too) */}
    //   {customer && (
    //     <div className="mfwc-buttons">
    //       {status === STATUS.APPROVAL_PENDING && payment_type === PAYMENT_TYPE.NEFT && (
    //         <>
    //           <div className="hover-btn" onClick={() => window.open(`/utr-submit/${payment_link}`, "_blank")}>
    //             <img src={linkButton} alt="linkButton" /> Input UTR
    //           </div>
    //           <div className="hover-btn" onClick={() => onCancel(data)}>
    //             <img src={cancelButton} alt="cancelButton" /> Cancel
    //           </div>
    //         </>
    //       )}
    //       {status === STATUS.APPROVAL_PENDING && payment_type === PAYMENT_TYPE.UPI && (
    //         <>
    //           <div className="hover-btn" onClick={() => onResend(data, PAYMENT_TYPE.UPI)}>
    //             <img src={linkButton} alt="linkButton" /> Resend Request
    //           </div>
    //           <div className="hover-btn" onClick={() => onCancel(data)}>
    //             <img src={cancelButton} alt="cancelButton" /> Cancel
    //           </div>
    //         </>
    //       )}
    //       {status === STATUS.APPROVAL_PENDING && payment_type === PAYMENT_TYPE.NETBANKING && (
    //         <>
    //           <div className="hover-btn" onClick={() => onResend(data, PAYMENT_TYPE.NETBANKING)}>
    //             <img src={linkButton} alt="linkButton" /> Resend Link
    //           </div>
    //           <div className="hover-btn" onClick={() => onCancel(data)}>
    //             <img src={cancelButton} alt="cancelButton" /> Cancel
    //           </div>
    //         </>
    //       )}
    //       {status === STATUS.APPROVAL_PENDING && type === 'Sell' && (
    //         <>
    //           <div className="hover-btn" onClick={() => onCancel(data)}>
    //             <img src={cancelButton} alt="cancelButton" /> Cancel
    //           </div>
    //         </>
    //       )}

    //     </div>
    //   )}
    // </div>

    <div className="mf-watchlist-card">
      <div className="mfwc-config">
        <div className="mfwc-config-row">
          <p>{input_date}</p>
          <p>
            <Badge
              variant={
                status === "Completed"
                  ? "success"
                  : status === "Approval Pending" || status === "Pending"
                    ? "warning"
                    : "error"
              }
              size="xxxs"
            >
              {status}{" "}
            </Badge>
          </p>
        </div>
        <div className="mfwc-config-row">
          <p className="name">{fundName}</p>
          <p className="value">{priceFormat(amount)}</p>
        </div>
        <div className="mfwc-config-row">
          <p>
            ISIN&nbsp;-&nbsp;<span>{isin}</span>
          </p>
          <p>
            Folio <span>{folio_no}</span>
          </p>
        </div>
        <div className="mfwc-config-row">
          <p>
            {numberFormat(units)}&nbsp;@&nbsp;{priceFormat(tran_nav)}
          </p>
          <p>
            <span
              style={{ textTransform: "uppercase" }}
              className={type === "Buy" ? "positive" : "negative"}
            >
              •&nbsp;{type}&nbsp;
            </span>
            ,&nbsp;&nbsp;{tran_type}
          </p>
        </div>
      </div>
      {/* Actions (uncomment if you want buttons on mobile cards too) */}
      {customer && (
        <div className="mfwc-buttons">
          {status === STATUS.APPROVAL_PENDING &&
            payment_type === PAYMENT_TYPE.NEFT && (
              <>
                <div
                  className="hover-btn"
                  onClick={() =>
                    window.open(`/utr-submit/${payment_link}`, "_blank")
                  }
                >
                  <img src={linkButton} alt="linkButton" /> Input UTR
                </div>
                <div className="hover-btn" onClick={() => onCancel(data)}>
                  <img src={cancelButton} alt="cancelButton" /> Cancel
                </div>
              </>
            )}
          {status === STATUS.APPROVAL_PENDING &&
            payment_type === PAYMENT_TYPE.UPI && (
              <>
                <div
                  className="hover-btn"
                  onClick={() => onResend(data, PAYMENT_TYPE.UPI)}
                >
                  <img src={linkButton} alt="linkButton" /> Resend Request
                </div>
                <div className="hover-btn" onClick={() => onCancel(data)}>
                  <img src={cancelButton} alt="cancelButton" /> Cancel
                </div>
              </>
            )}
          {status === STATUS.APPROVAL_PENDING &&
            payment_type === PAYMENT_TYPE.NETBANKING && (
              <>
                <div
                  className="hover-btn"
                  onClick={() => onResend(data, PAYMENT_TYPE.NETBANKING)}
                >
                  <img src={linkButton} alt="linkButton" /> Resend Link
                </div>
                <div className="hover-btn" onClick={() => onCancel(data)}>
                  <img src={cancelButton} alt="cancelButton" /> Cancel
                </div>
              </>
            )}
          {status === STATUS.APPROVAL_PENDING && type === "Sell" && (
            <>
              <div className="hover-btn" onClick={() => onCancel(data)}>
                <img src={cancelButton} alt="cancelButton" /> Cancel
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** =============================
 *  Suggestions
 *  ============================= */
function StockSuggestion({ search, loading, suggestions, handleSelect }) {
  if (!Array.isArray(suggestions)) suggestions = [];

  if (loading) {
    return (
      <div className="search__loader">
        <Pulse />
{/* <p>Loading Mutual Funds Please Wait ...</p> */}
      </div>
    );
  }

  if (suggestions.length > 0) {
    return (
      <>
        {suggestions.map((s, i) => (
          <p
            key={`${s.isin}=${s.Scheme_Type || ""}=${s.Scheme_Code || ""}=${s.Unique_No || i}`}
            onClick={() => handleSelect(s)}
          >
            <span>{s.fund_name}</span>
          </p>
        ))}
      </>
    );
  }

  return (
    <div className="search__loader">
      {search.length === 0 ? (
        <>
          <Pulse />
{/* <p>No recent searches</p> */}
          <p>Hit Me...</p>
        </>
      ) : (
        <p>
          No result for &nbsp;<strong>'{search.toUpperCase()}'</strong>
        </p>
      )}
    </div>
  );
}
